/* iter-p2-d — Feature Videos admin panel.
 *
 * Grid over the FEATURE_CATALOG from `/api/feature-videos` (public
 * read; catalog is the source of truth). One row per feature, grouped
 * by area, with a URL input, a Preview button (opens the same
 * `VideoOverlay` used by the Training Center), and a single Save
 * button that PUTs the whole map to `/api/admin/feature-videos`.
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  PlayCircle, Save, Loader2, Video, ExternalLink, Search,
} from 'lucide-react';
import { VideoOverlay } from '@/components/VideoOverlay';
import {
  useFeatureVideoCatalog, invalidateFeatureVideosCache,
} from '@/lib/useFeatureVideos';

const API = (process.env.REACT_APP_BACKEND_URL || '') + '/api';

function looksValidUrl(u) {
  if (!u) return true;
  try { const p = new URL(u); return p.protocol === 'http:' || p.protocol === 'https:'; }
  catch { return false; }
}

export default function FeatureVideosAdminPanel() {
  const { catalog, videos, loading } = useFeatureVideoCatalog();
  const [draft, setDraft]   = useState({});      // slug -> url string
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(null);  // { title, url }
  const [filter, setFilter] = useState('');

  // Hydrate the draft once the fetch settles.
  useEffect(() => {
    if (!loading) setDraft({ ...videos });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  const grouped = useMemo(() => {
    const q = filter.trim().toLowerCase();
    const out = {};
    for (const row of catalog) {
      if (q) {
        const hay = `${row.slug} ${row.title} ${row.blurb || ''} ${row.area || ''}`.toLowerCase();
        if (!hay.includes(q)) continue;
      }
      const area = row.area || 'Other';
      (out[area] ||= []).push(row);
    }
    return out;
  }, [catalog, filter]);

  const setUrl = useCallback((slug, url) => {
    setDraft((prev) => ({ ...prev, [slug]: url }));
  }, []);

  const dirtyCount = useMemo(() => {
    let n = 0;
    const keys = new Set([...Object.keys(draft), ...Object.keys(videos)]);
    for (const k of keys) if ((draft[k] || '') !== (videos[k] || '')) n += 1;
    return n;
  }, [draft, videos]);

  const save = async () => {
    // Validate every non-empty URL.
    for (const [slug, url] of Object.entries(draft)) {
      if (url && !looksValidUrl(url)) {
        toast.error(`"${slug}" — URL must start with http(s)://`);
        return;
      }
    }
    setSaving(true);
    try {
      const payload = Object.fromEntries(
        Object.entries(draft).filter(([, v]) => v && v.trim()),
      );
      await axios.put(`${API}/admin/feature-videos`, { videos: payload });
      invalidateFeatureVideosCache();
      toast.success(`Saved feature videos${dirtyCount ? ` · ${dirtyCount} change${dirtyCount === 1 ? '' : 's'}` : ''}.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12" data-testid="admin-feature-videos-loading">
        <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
      </div>
    );
  }

  const areas = Object.keys(grouped);
  const totalRows = catalog.length;
  const boundCount = Object.values(draft).filter((v) => v && v.trim()).length;

  return (
    <div className="mx-auto max-w-4xl space-y-4 p-4" data-testid="admin-feature-videos-panel">
      <div className="flex items-start gap-2">
        <Video className="mt-0.5 h-5 w-5 text-primary" />
        <div className="flex-1">
          <h2 className="text-[18px] font-semibold"
              style={{ fontFamily: 'Cormorant Garamond, serif' }}>
            Feature Videos · one 30-second explainer per tool
          </h2>
          <p className="text-[11.5px] text-muted-foreground">
            Bind a YouTube / Loom / MP4 URL to any feature slug. Writers see a
            <b> Watch</b> button when they right-click the tool's HelpTip. Empty
            rows leave the tooltip text-only.
          </p>
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md border border-border bg-panel-header/40 p-2">
        <div className="flex items-center gap-2 text-[11.5px] text-muted-foreground">
          <span data-testid="admin-feature-videos-stats">
            <b className="text-foreground">{boundCount}</b> / {totalRows} features have a video
          </span>
          {dirtyCount > 0 && (
            <span className="rounded-sm bg-amber-500/10 px-1.5 py-0.5 text-[10.5px] text-amber-800 dark:text-amber-200"
                  data-testid="admin-feature-videos-dirty">
              {dirtyCount} unsaved
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter features…"
              className="h-8 w-56 pl-7 text-[12px]"
              data-testid="admin-feature-videos-filter"
            />
          </div>
          <Button
            onClick={save}
            disabled={saving || dirtyCount === 0}
            className="gap-1"
            data-testid="admin-feature-videos-save"
          >
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5" />}
            Save
          </Button>
        </div>
      </div>

      {areas.length === 0 && (
        <div className="rounded-md border border-dashed border-border p-6 text-center text-[11.5px] text-muted-foreground"
             data-testid="admin-feature-videos-empty-filter">
          No features match “{filter}”.
        </div>
      )}

      {areas.map((area) => (
        <section key={area}
                 className="rounded-md border border-border bg-card"
                 data-testid={`admin-feature-videos-area-${area.replace(/\s+/g, '-').toLowerCase()}`}>
          <header className="border-b border-border bg-panel-header/30 px-3 py-1.5 text-[11px] uppercase tracking-wider text-primary">
            {area} · {grouped[area].length}
          </header>
          <div className="divide-y divide-border">
            {grouped[area].map((row) => {
              const url = draft[row.slug] ?? '';
              const bad = !looksValidUrl(url);
              const original = videos[row.slug] || '';
              const dirty = url !== original;
              return (
                <div key={row.slug}
                     className="grid grid-cols-1 gap-2 p-3 sm:grid-cols-[minmax(0,1fr)_minmax(0,1.4fr)_auto]"
                     data-testid={`admin-feature-videos-row-${row.slug}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-foreground">
                      {row.title}
                      {dirty && <span className="rounded-sm bg-amber-500/15 px-1 py-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-300">edited</span>}
                    </div>
                    <div className="text-[10.5px] text-muted-foreground font-mono-stat">{row.slug}</div>
                    {row.blurb && (
                      <p className="mt-0.5 text-[11px] leading-snug text-muted-foreground">{row.blurb}</p>
                    )}
                  </div>
                  <div className="min-w-0">
                    <Input
                      value={url}
                      onChange={(e) => setUrl(row.slug, e.target.value)}
                      placeholder="https://youtu.be/… or https://www.loom.com/share/…"
                      className={`h-8 text-[12px] ${bad ? 'border-destructive' : ''}`}
                      data-testid={`admin-feature-videos-input-${row.slug}`}
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      size="sm"
                      variant="outline"
                      className="h-8 gap-1 text-[11px]"
                      disabled={!url || bad}
                      onClick={() => setPreview({ title: row.title, url })}
                      data-testid={`admin-feature-videos-preview-${row.slug}`}
                    >
                      <PlayCircle className="h-3.5 w-3.5" />
                      Preview
                    </Button>
                    {url && !bad && (
                      <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-sm p-1 text-muted-foreground hover:text-primary"
                        data-testid={`admin-feature-videos-open-${row.slug}`}
                        aria-label="Open in a new tab"
                        title="Open in a new tab"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      ))}

      {preview && (
        <VideoOverlay
          title={preview.title}
          url={preview.url}
          onClose={() => setPreview(null)}
          testIdPrefix="admin-feature-videos-video"
        />
      )}
    </div>
  );
}
